import type { HeroSeatSlot, PlayingCard, PlayingCardRank, PlayingCardSuit, PokerChipThrowProfile, PokerSeat, PokerState, PokerTableCard, PokerTableChip, TableEdge } from "../types";
import { uid } from "./defaults";
import { addLoosePokerChipPhysics, pokerContributionPlacements, pokerContributionValues } from "./poker-chip-layout";
import { DEFAULT_POKER_TABLE_STYLE } from "./poker-table-styles";
import { createPokerBlindMarkers, normalizedPokerBlindMarkers } from "./poker-blinds";

export const POKER_SUITS: readonly PlayingCardSuit[] = ["clubs", "diamonds", "hearts", "spades"];
export const POKER_RANKS: readonly PlayingCardRank[] = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K", "A"];
export const POKER_SEAT_COLORS = ["#4cc9f0", "#f72585", "#f9c74f", "#90be6d", "#b892ff", "#f9844a", "#43aa8b", "#e9c46a"] as const;
export const MAX_POKER_PLAYERS = 8;
const EDGE_ORDER: readonly TableEdge[] = ["north", "east", "south", "west"];
const SLOT_ORDER: readonly HeroSeatSlot[] = ["left", "right", "center"];
export const POKER_CARD_STACK_ROW_CAPACITY = 5;
export const POKER_CARD_LEFT_INDEX_MIN_REVEAL_WORLD = 24;
export const POKER_SIDE_CARD_CENTER_LANE_LIMIT = .75;
export const POKER_PAYOUT_GLIDE_DURATION_MS = 1_650;
export const POKER_PAYOUT_SETTLEMENT_BUFFER_MS = 300;
export const POKER_FOLD_GLIDE_DURATION_MS = 1_350;
export const POKER_FOLD_SETTLEMENT_BUFFER_MS = 400;

export function freshDeck(): PlayingCard[] {
  return POKER_SUITS.flatMap((suit) => POKER_RANKS.map((rank) => ({ id: uid("card"), suit, rank })));
}

export function shuffleCards(cards: PlayingCard[]) {
  const shuffled = cards.map((card) => ({ ...card }));
  const random = new Uint32Array(1);
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    crypto.getRandomValues(random); const target = random[0] % (index + 1);
    [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
  }
  return shuffled;
}

export function createPokerState(now = Date.now()): PokerState {
  return { deck: shuffleCards(freshDeck()), seats: [], tableCards: [], tableChips: [], pot: 0, chipLayoutMode: "loose", tableStyle: DEFAULT_POKER_TABLE_STYLE, tableDisplayMode: "table", tableEdgeShade: 42, blindMarkers: createPokerBlindMarkers(now), handNumber: 1, updatedAt: now };
}

export function nextPokerSeatPosition(seats: PokerSeat[]): { edge: TableEdge; slot: HeroSeatSlot } {
  const index = seats.length;
  return { edge: EDGE_ORDER[index % EDGE_ORDER.length], slot: SLOT_ORDER[Math.floor(index / EDGE_ORDER.length) % SLOT_ORDER.length] };
}

export function createPokerSeat(name: string, seats: PokerSeat[], chips = 1_000): PokerSeat {
  const position = nextPokerSeatPosition(seats);
  return { id: uid("poker-seat"), name: name.trim() || `Player ${seats.length + 1}`, color: POKER_SEAT_COLORS[seats.length % POKER_SEAT_COLORS.length], ...position, chips, committedChips: 0, folded: false, hand: [] };
}

export function addPokerSeat(state: PokerState, name: string, now = Date.now()): PokerState {
  const safeName = name.trim();
  if (!safeName || state.seats.length >= MAX_POKER_PLAYERS) return state;
  return { ...state, seats: [...state.seats, createPokerSeat(safeName, state.seats)], updatedAt: now };
}

function cardPlacementImperfection(cardId: string) {
  let seed = 2166136261; for (let index = 0; index < cardId.length; index += 1) { seed ^= cardId.charCodeAt(index); seed = Math.imul(seed, 16777619); }
  const random = () => { seed += 0x6d2b79f5; let mixed = seed; mixed = Math.imul(mixed ^ mixed >>> 15, mixed | 1); mixed ^= mixed + Math.imul(mixed ^ mixed >>> 7, mixed | 61); return ((mixed ^ mixed >>> 14) >>> 0) / 4294967296; };
  return { tangent: (random() - .5) * .009, depth: (random() - .5) * .006, rotation: (random() - .5) * .028 };
}

function tablePosition(index: number, cardId: string) {
  const column = index % 7; const row = Math.floor(index / 7);
  const imperfect = cardPlacementImperfection(cardId); return { x: .5 + (column - 3) * .082 + imperfect.tangent, y: .5 + row * .13 + imperfect.depth, rotation: (column - 3) * .018 + imperfect.rotation };
}

export function pokerPlayedCardPosition(seat: Pick<PokerSeat, "edge" | "slot">, index: number, cardId: string) {
  const slot = seat.slot === "left" ? -1 : seat.slot === "right" ? 1 : 0; const column = index % POKER_CARD_STACK_ROW_CAPACITY; const row = Math.floor(index / POKER_CARD_STACK_ROW_CAPACITY); const imperfect = cardPlacementImperfection(cardId);
  // Every row advances in the card's local-right direction while newer cards
  // render above older cards. That guarantees the complete left rank/suit band
  // remains exposed instead of alternating which edge gets covered.
  if (seat.edge === "north") return { x: .5 + slot * .24 - column * .031 - imperfect.tangent, y: .29 + row * .13 + imperfect.depth, rotation: Math.PI + imperfect.rotation };
  if (seat.edge === "south") return { x: .5 - slot * .24 + column * .031 + imperfect.tangent, y: .71 - row * .13 + imperfect.depth, rotation: imperfect.rotation };
  // Side hands belong in the narrow lane between the central deal and the
  // player's private hand. Keep later rows within a small, bounded inset: the
  // former .1-per-row drift pulled east/west cards directly over dealt cards.
  const sideRowInset = Math.min(row, 2) * .02;
  if (seat.edge === "east") return { x: .805 - sideRowInset + imperfect.depth, y: .5 + slot * .245 + column * .05 + imperfect.tangent, rotation: -Math.PI / 2 + imperfect.rotation };
  return { x: .195 + sideRowInset + imperfect.depth, y: .5 - slot * .245 - column * .05 - imperfect.tangent, rotation: Math.PI / 2 + imperfect.rotation };
}

export function dealOneToEverySeat(state: PokerState, now = Date.now()): PokerState {
  if (state.foldAnimation) return state;
  const deck = [...state.deck];
  const seats = state.seats.map((seat) => {
    if (seat.folded) return seat;
    const card = deck.shift(); return card ? { ...seat, hand: [...seat.hand, card] } : seat;
  });
  return { ...state, deck, seats, updatedAt: now };
}

export function dealCardToSeat(state: PokerState, seatId: string, now = Date.now()): PokerState {
  const seat = state.seats.find((candidate) => candidate.id === seatId); const card = state.deck[0]; if (!card || !seat || seat.folded || state.foldAnimation) return state;
  return { ...state, deck: state.deck.slice(1), seats: state.seats.map((seat) => seat.id === seatId ? { ...seat, hand: [...seat.hand, card] } : seat), updatedAt: now };
}

export function drawDeckCardToTable(state: PokerState, now = Date.now()): PokerState {
  const card = state.deck[0]; if (!card || state.foldAnimation) return state;
  const placement = tablePosition(state.tableCards.length, card.id);
  const tableCard: PokerTableCard = { id: uid("table-card"), card, ...placement, thrownAt: now };
  return { ...state, deck: state.deck.slice(1), tableCards: [...state.tableCards, tableCard], updatedAt: now };
}

export function playSeatCard(state: PokerState, seatId: string, cardId: string, now = Date.now(), throwProfile?: PokerChipThrowProfile): PokerState {
  const seat = state.seats.find((candidate) => candidate.id === seatId); const card = seat?.hand.find((candidate) => candidate.id === cardId);
  if (!seat || !card || seat.folded || state.foldAnimation) return state;
  const placement = pokerPlayedCardPosition(seat, state.tableCards.filter((candidate) => candidate.sourceSeatId === seatId).length, card.id);
  const tableCard: PokerTableCard = { id: uid("table-card"), card, sourceSeatId: seatId, ...placement, thrownAt: now, throwProfile: normalizePokerThrowProfile(throwProfile) };
  return { ...state, seats: state.seats.map((candidate) => candidate.id === seatId ? { ...candidate, hand: candidate.hand.filter((item) => item.id !== cardId) } : candidate), tableCards: [...state.tableCards, tableCard], updatedAt: now };
}

/** Sends an owned set of cards to the table as one remote hand gesture. */
export function playSeatCards(state: PokerState, seatId: string, cardIds: string[], now = Date.now(), throwProfile?: PokerChipThrowProfile): PokerState {
  const seat = state.seats.find((candidate) => candidate.id === seatId); const owned = new Set(seat?.hand.map((card) => card.id) ?? []);
  if (!seat || seat.folded || state.foldAnimation || !cardIds.length || new Set(cardIds).size !== cardIds.length || cardIds.some((cardId) => !owned.has(cardId))) return state;
  return cardIds.reduce((current, cardId) => playSeatCard(current, seatId, cardId, now, throwProfile), state);
}

/** Reorders only a complete permutation of the requesting player's private hand. */
export function reorderPokerSeatHand(state: PokerState, seatId: string, cardIds: string[], now = Date.now()): PokerState {
  const seat = state.seats.find((candidate) => candidate.id === seatId);
  if (!seat || seat.folded || state.foldAnimation || cardIds.length !== seat.hand.length || new Set(cardIds).size !== cardIds.length) return state;
  const cardsById = new Map(seat.hand.map((card) => [card.id, card]));
  if (cardIds.some((cardId) => !cardsById.has(cardId))) return state;
  const hand = cardIds.map((cardId) => cardsById.get(cardId)!);
  if (hand.every((card, index) => card.id === seat.hand[index]?.id)) return state;
  return { ...state, seats: state.seats.map((candidate) => candidate.id === seatId ? { ...candidate, hand } : candidate), updatedAt: now };
}

const finiteClamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, Number.isFinite(value) ? value : minimum));

export function normalizePokerThrowProfile(profile?: PokerChipThrowProfile): PokerChipThrowProfile | undefined {
  if (!profile) return undefined;
  return { durationMs: Math.round(finiteClamp(profile.durationMs, 70, 1_800)), speed: finiteClamp(profile.speed, .05, 2), curve: finiteClamp(profile.curve, -1, 1), releaseX: finiteClamp(profile.releaseX, -1, 1), releaseY: finiteClamp(profile.releaseY, .05, 1) };
}

export function commitPokerChips(state: PokerState, seatId: string, amount: number, now = Date.now(), throwProfile?: PokerChipThrowProfile): PokerState {
  if (state.payoutAnimation || state.foldAnimation) return state;
  const seat = state.seats.find((candidate) => candidate.id === seatId); const safeAmount = Math.max(0, Math.min(seat?.chips ?? 0, Math.round(amount)));
  if (!seat || seat.folded || !safeAmount) return state;
  const seatIndex = Math.max(0, state.seats.findIndex((candidate) => candidate.id === seatId)); const contributionIndex = state.tableChips.filter((chip) => chip.seatId === seatId).length;
  const column = seatIndex % 4; const row = Math.floor(seatIndex / 4); const rowCount = Math.min(4, state.seats.length - row * 4); const pileAngle = contributionIndex * 2.399963; const pileRadius = Math.sqrt(contributionIndex) * .012;
  // Pot contributions live in compact per-player piles below the central card
  // lane. Stored coordinates remain useful to exports and future renderers.
  const chip: PokerTableChip = { id: uid("table-chip"), seatId, amount: safeAmount, x: .5 + (column - (rowCount - 1) / 2) * .12 + Math.cos(pileAngle) * pileRadius, y: .68 + row * .1 + Math.sin(pileAngle) * pileRadius, thrownAt: now, throwProfile: normalizePokerThrowProfile(throwProfile) };
  const debited = { ...state, seats: state.seats.map((candidate) => candidate.id === seatId ? { ...candidate, chips: candidate.chips - safeAmount, committedChips: candidate.committedChips + safeAmount } : candidate), pot: state.pot + safeAmount, updatedAt: now };
  return addLoosePokerChipPhysics(debited, chip, now);
}

export type PokerPotShare = { seatId: string; amount: number };

/** Equal pot shares in table order, beginning left of the dealer for odd chips. */
export function pokerPotShares(state: PokerState, seatIds: string[]): PokerPotShare[] {
  const pot = Math.max(0, Math.round(state.pot)); const selected = new Set(seatIds); const dealerIndex = state.dealerSeatId ? state.seats.findIndex((seat) => seat.id === state.dealerSeatId) : -1;
  const orderedSeats = dealerIndex >= 0 ? [...state.seats.slice(dealerIndex + 1), ...state.seats.slice(0, dealerIndex + 1)] : state.seats;
  const recipients = orderedSeats.filter((seat) => selected.has(seat.id));
  if (!pot || recipients.length < 1 || recipients.length > MAX_POKER_PLAYERS || pot < recipients.length) return [];
  const base = Math.floor(pot / recipients.length); const remainder = pot % recipients.length;
  return recipients.map((seat, index) => ({ seatId: seat.id, amount: base + (index < remainder ? 1 : 0) }));
}

/** Keeps the pot authoritative while exact-value chips glide to every winner. */
export function beginPokerPayout(state: PokerState, seatIds: string[], now = Date.now()): PokerState {
  if (state.payoutAnimation || state.foldAnimation) return state;
  const shares = pokerPotShares(state, seatIds); if (!shares.length) return state;
  const payoutId = uid("poker-payout");
  const sources = state.tableChips.flatMap((chip) => pokerContributionPlacements(chip));
  const fallback = { x: .5, y: .68, stackLevel: 0, rotation: 0, tiltX: 0, tiltY: 0 };
  let outgoingIndex = 0;
  const chips = shares.flatMap((share, recipientIndex) => pokerContributionValues(share.amount).map((value, recipientChipIndex) => {
    const base = sources.length ? sources[outgoingIndex % sources.length] : fallback; const reusedLayer = sources.length ? Math.floor(outgoingIndex / sources.length) : outgoingIndex; const angle = outgoingIndex * 2.399963 + recipientIndex * .71; const radius = reusedLayer ? Math.min(18, 3 + reusedLayer * 3) : 0;
    const source = { ...base, x: finiteClamp(base.x + Math.cos(angle) * radius / 1120, .1, .9), y: finiteClamp(base.y + Math.sin(angle) * radius / 680, .58, .92), stackLevel: Math.max(0, base.stackLevel + reusedLayer), tiltX: 0, tiltY: 0, motion: undefined };
    outgoingIndex += 1;
    return { id: `${payoutId}:${recipientIndex}:${recipientChipIndex}`, recipientSeatId: share.seatId, recipientIndex, recipientChipIndex, value, source };
  }));
  return { ...state, payoutAnimation: { id: payoutId, startedAt: now, durationMs: POKER_PAYOUT_GLIDE_DURATION_MS, shares, chips }, updatedAt: now };
}

/** Completes one matching staged payout after its Player Display glide. */
export function settlePokerPayout(state: PokerState, payoutId: string, now = Date.now()): PokerState {
  const payout = state.payoutAnimation; if (!payout || payout.id !== payoutId) return state;
  const bySeat = new Map(payout.shares.map((share) => [share.seatId, share.amount]));
  return { ...state, seats: state.seats.map((seat) => ({ ...seat, chips: seat.chips + (bySeat.get(seat.id) ?? 0) })), tableChips: [], pot: 0, chipLayoutMode: "loose", payoutAnimation: undefined, updatedAt: now };
}

/** Immediate award helper for non-animated state callers. */
export function awardPokerPot(state: PokerState, seatIds: string[], now = Date.now()): PokerState {
  const staged = beginPokerPayout(state, seatIds, now); if (staged === state || !staged.payoutAnimation) return state;
  return settlePokerPayout(staged, staged.payoutAnimation.id, now);
}

/** Moves a private hand into the DM-only muck and stages a count-only glide. */
export function foldPokerSeat(state: PokerState, seatId: string, now = Date.now()): PokerState {
  const seat = state.seats.find((candidate) => candidate.id === seatId);
  if (!seat || seat.folded || !seat.hand.length || state.foldAnimation || state.payoutAnimation) return state;
  const foldedCards = seat.hand.map((card) => ({ ...card }));
  const animation = { id: uid("poker-fold"), seatId, cardCount: foldedCards.length, startedAt: now, durationMs: POKER_FOLD_GLIDE_DURATION_MS };
  return {
    ...state,
    muck: [...(state.muck ?? []), ...foldedCards],
    seats: state.seats.map((candidate) => candidate.id === seatId ? { ...candidate, hand: [], folded: true } : candidate),
    foldAnimation: animation,
    lastFoldAnimation: animation,
    updatedAt: now,
  };
}

/** Clears only matching visual metadata; the cards remain safely in the muck. */
export function clearPokerFoldAnimation(state: PokerState, foldId: string, now = Date.now()): PokerState {
  if (!state.foldAnimation || state.foldAnimation.id !== foldId) return state;
  return { ...state, foldAnimation: undefined, updatedAt: now };
}

export function startNextPokerHand(state: PokerState, now = Date.now()): PokerState {
  const returnedCards = [...state.deck, ...(state.muck ?? []), ...state.tableCards.map((item) => item.card), ...state.seats.flatMap((seat) => seat.hand)];
  return { ...state, deck: shuffleCards(returnedCards.length === 52 ? returnedCards : freshDeck()), muck: [], seats: state.seats.map((seat) => ({ ...seat, hand: [], committedChips: 0, folded: false })), tableCards: [], tableChips: [], pot: 0, chipLayoutMode: "loose", payoutAnimation: undefined, foldAnimation: undefined, lastFoldAnimation: undefined, handNumber: state.handNumber + 1, updatedAt: now };
}

export function publicPokerState(state: PokerState) {
  return { deckCount: state.deck.length, muckCount: state.muck?.length ?? 0, seats: state.seats.map(({ hand, ...seat }) => ({ ...seat, handCount: hand.length })), tableCards: state.tableCards, tableChips: state.tableChips, pot: state.pot, chipLayoutMode: state.chipLayoutMode, tableStyle: state.tableStyle ?? DEFAULT_POKER_TABLE_STYLE, tableDisplayMode: state.tableDisplayMode ?? "table", tableEdgeShade: Math.max(-100, Math.min(100, state.tableEdgeShade ?? 42)), payoutAnimation: state.payoutAnimation, foldAnimation: state.foldAnimation, lastFoldAnimation: state.lastFoldAnimation, dealerSeatId: state.dealerSeatId, blindMarkers: normalizedPokerBlindMarkers(state), handNumber: state.handNumber, updatedAt: state.updatedAt };
}

export function cardLabel(card: PlayingCard) {
  const suit = { clubs: "♣", diamonds: "♦", hearts: "♥", spades: "♠" }[card.suit];
  return `${card.rank}${suit}`;
}
